import { useEffect } from 'react';
import {
  adminActions,
  adminApproveData,
  adminRequestContractDetails as getRequestContractDetails,
  adminContractDetails,
  bookAvailabilityData,
  getApproveDetails,
  getBookAvailablity,
  resetStatus,
  ServiceTypes,
  successSelector,
  IAdminApproveDetailsData,
  IAdminRequestDetailsContractModel,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import {
  renderScreen,
  resetDrawer,
  setBack,
} from '../../actions/drawerActions';
import { IApproveBooking } from '../../components/Admin/ApproveBooking/ApproveBooking';
import { OtherScreens, ScreenTypes } from '../../helpers/Constants';
import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import { showSuccess } from '../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import strings from '../../localization';
import useUpdateContract from '../LongTermContract/useUpdateContract';

const {
  bookAvailablity: bookAvailablityAction,
  approveBooking,
  selfBookAvailablity: selfBookAvailablityAction,
  selfApproveBooking,
  adminApproveContract,
  adminSelfApproveContract,
} = adminActions;
const { self, full } = ServiceTypes;

const useApproveBookingUtils = ({
  workshiftId,
  eventIds,
  bookingId,
  fromScreen,
  type,
  back,
  professionalId,
  isAutoSyncRecord,
  longTermShiftContractId,
}: IApproveBooking) => {
  const dispatch = useAppDispatch();
  const { setAttributes } = useUpdateContract();

  const approveDetails = useAppSelector(adminApproveData);
  const bookAvailability = useAppSelector(bookAvailabilityData);
  const longTermDetails = useAppSelector(adminContractDetails);

  const serviceSuccessActions = {
    [self]: {
      bookAvailability: selfBookAvailablityAction,
      approveBooking: selfApproveBooking,
      longTermBooking: adminSelfApproveContract,
    },
    [full]: {
      bookAvailability: bookAvailablityAction,
      approveBooking,
      longTermBooking: adminApproveContract,
    },
  };

  const { bookAvailability: bookAction, approveBooking: approveAction, longTermBooking } = serviceSuccessActions[ServiceHandler.getService()];

  const bookingSuccess = useAppSelector((state) =>
    successSelector([approveAction], state));
  const bookingAvailabilitySuccess = useAppSelector((state) =>
    successSelector([bookAction], state));
  const longTermBookingSuccess = useAppSelector((state) =>
    successSelector([longTermBooking], state));

  const isBookAvailability = fromScreen === OtherScreens.MatchingAvailabilities
    || fromScreen === OtherScreens.MatchingWorkshifts;

  const goToRequestPharmacist = () => {
    dispatch(
      renderScreen({
        screenType: ScreenTypes.requestedList,
        screenNumber: 2,
        eventId: workshiftId,
        extraParameters: {
          isAutoSyncRecord,
        },
      }),
    );
  };

  const goToRequestedDetails = () => {
    dispatch(
      renderScreen({
        screenType: ScreenTypes.requestedList,
        screenNumber: 3,
        workshiftId,
        eventId: bookingId,
        eventIds,
        extraParameters: {
          isAutoSyncRecord,
        },
      }),
    );
  };

  const goToMatchingAvailabilities = () => {
    dispatch(
      renderScreen({
        screenType: ScreenTypes.availableWorkshift,
        screenNumber: 4,
        eventId: workshiftId,
        type,
      }),
    );
  };

  const goBack = () => {
    switch (fromScreen) {
      case OtherScreens.MatchingAvailabilities:
        return goToMatchingAvailabilities;
      case OtherScreens.AdminCalendar:
        return goToRequestPharmacist;
      case OtherScreens.AdminMap:
        return goToRequestedDetails;
      default:
        return goToMatchingAvailabilities;
    }
  };

  const config = () => {
    let dynamicConfig: {
      success: boolean;
      dispatchAction: () => void;
      approveDetails: IAdminApproveDetailsData | IAdminRequestDetailsContractModel
    } = {
      success: bookingSuccess,
      dispatchAction: () => {
        dispatch(getApproveDetails({ id: bookingId, isAutoSyncRecord }));
      },
      approveDetails,
    };

    if (isBookAvailability) {
      dynamicConfig = {
        success: bookingAvailabilitySuccess,
        dispatchAction: () => {
          dispatch(
            getBookAvailablity({ workshiftId, availabilityId: bookingId }),
          );
        },
        approveDetails: bookAvailability,
      };
    }

    if (longTermShiftContractId) {
      dynamicConfig = {
        success: longTermBookingSuccess,
        dispatchAction: () => {
          dispatch(
            getRequestContractDetails({ longTermContractId: longTermShiftContractId, professionalId: professionalId ?? '' }),
          );
        },
        approveDetails: longTermDetails,
      };
    }

    return dynamicConfig;
  };

  const { success, approveDetails: details, dispatchAction } = config() ?? {};

  useEffect(() => {
    dispatchAction();
    if (fromScreen !== OtherScreens.AdminRequestedReports) {
      dispatch(setBack(back ?? goBack()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, workshiftId]);

  useEffect(() => {
    if (success) {
      dispatch(resetStatus([bookAction, approveAction, longTermBooking]));
      dispatch(resetDrawer());
      showSuccess(dispatch, strings.approvedSuccess);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  useEffect(() => {
    const { longTermShiftContract } = details as IAdminRequestDetailsContractModel ?? {};
    const { startDate, endDate } = longTermShiftContract ?? {};

    if (longTermShiftContractId) {
      setAttributes(startDate, endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  return { details };
};

export default useApproveBookingUtils;
